<template>
  <v-card
    :max-width="$vuetify.breakpoint.mobile ? '' : '400px'"
    min-height="630"
    class="mb-8"
  >
    <v-app-bar dark color="primary" color2="pink">
      <v-toolbar-title>{{ report.voyageNo }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <span>
        {{
          report.latestActivity.startDateTimeLt | removeTAndSecondsAndFormatDate
        }}
      </span>
    </v-app-bar>
    <v-hover v-slot="{ hover }">
      <v-img
        @click="goToVoyageDetails(report.id)"
        v-if="report.vesselImageUrl"
        :src="report.vesselImageUrl"
        class="white--text align-end report-card-header"
        :gradient="hover ? hoverImageGradient : imageGradient"
        height="200px"
      >
        <div class="d-flex justify-space-between">
          <v-card-title v-text="report.vesselName"></v-card-title>

          <v-tooltip left color="black">
            <template v-slot:activator="{ on, attrs }">
              <v-card-title v-on="on" v-bind="attrs">
                <v-icon left color="white">mdi-account-group</v-icon>
                {{ report.passengers.onBoard }}
              </v-card-title>
            </template>
            <span>Total Currently On Board</span>
          </v-tooltip>

          <v-tooltip left color="black">
            <template v-slot:activator="{ on, attrs }">
              <v-card-title v-on="on" v-bind="attrs">
                <v-icon left color="white">mdi-comment-alert</v-icon>
                {{ report.safetyObservations.total }}
              </v-card-title>
            </template>
            <span>Total Current Safety Observations</span>
          </v-tooltip>
        </div>
      </v-img>
    </v-hover>
    <v-tabs
      class="mb-4"
      background-color="background"
      v-model="tab"
      fixed-tabs
      hide-slider
      active-class="active-tab"
    >
      <v-tab href="#activity">
        <span
          v-if="report.latestActivity.endDateTimeLt"
          v-text="'Last Activity'"
        >
        </span>
        <span v-else v-text="'In Progress'"></span>
        <v-spacer />
        <v-tooltip v-if="isActivityInvalid(report)" bottom color="black">
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="warning" v-bind="attrs" v-on="on">
              mdi-alert-circle</v-icon
            >
          </template>
          <span>{{ getInvalidActivityIssueMessage(report) }}</span>
        </v-tooltip>
      </v-tab>
      <v-tab href="#recent">
        <span>Recent</span>
        <v-spacer />
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item :value="mainTabValue">
        <v-card-text class="d-flex flex-column text-start pt-0">
          <span class="text-body-2 text-center">
            <span class="font-weight-bold"> Start Time (LT): </span>
            {{
              report.latestActivity.startDateTimeLt | removeTAndDateAndSeconds
            }}
            <span>|</span>
            <span class="font-weight-bold"> End Time (LT): </span>
            {{
              report.latestActivity.endDateTimeLt
                ? $options.filters.removeTAndDateAndSeconds(
                    report.latestActivity.endDateTimeLt
                  )
                : "Ongoing"
            }}
          </span>
          <div class="my-8">
            <div class="text-h4">
              {{ report.latestActivity.code }}
              <v-divider />
            </div>
            <div class="text-h5 my-2 d-block text-truncate">
              {{ report.latestActivity.activityType }}
            </div>
          </div>
          <div>
            <v-divider />
            <div class="my-2">
              <span class="font-weight-bold">Location:</span>
              {{ report.latestActivity.locationName }}
            </div>
            <v-divider />
            <div class="my-2">
              <span class="font-weight-bold">Elapsed Time (mins):</span>
              {{ getActivityElapsedTimeMins(report.latestActivity) }}
            </div>
            <v-divider />
            <div class="mt-2">
              <v-icon v-if="isUpdateWarning" color="error" left
                >mdi-alert-circle</v-icon
              >
              <span class="font-weight-bold">Last Update (Lt):</span>
              {{
                report.latestActivity.lastUpdatedAtLt
                  | removeTAndSecondsAndFormatDate
              }}
            </div>
          </div>
        </v-card-text>
      </v-tab-item>
      <v-tab-item :value="activitiesTabValue">
        <v-list-item
          v-for="activity in recentActivities"
          :key="activity.id"
          two-line
          class="text-start"
        >
          <v-list-item-content>
            <v-list-item-title
              >{{ activity.code }} -
              {{ activity.activityType }}</v-list-item-title
            >
            <v-list-item-subtitle>
              <span
                >{{ activity.startDateTimeLt | removeTAndSecondsAndFormatDate }}
                <v-icon small color="primary"> mdi-clock-start </v-icon>
                <span v-if="activity.endDateTimeLt">
                  {{ activity.endDateTimeLt | removeTAndSecondsAndFormatDate }}
                </span>
                <span v-else> In Progress </span>
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
export default {
  name: 'VesselOverviewCard',
  props: {
    report: {
      type: Object,
      required: true,
    },
    overdueHours: Number,
    lastRefreshLt: String,
  },
  data() {
    return {
      tab: null,
      mainTabValue: 'activity',
      activitiesTabValue: 'recent',
    };
  },
  computed: {
    recentActivities() {
      const activities = this.report.vesselActivities;
      return activities.slice(Math.max(activities.length - 4, 0)).reverse();
    },
    isUpdateWarning() {
      return this.showUpdateWarning(this.report.latestActivity);
    },
    imageGradient() {
      return 'to bottom, rgba(0,0,0,.2), rgba(0,0,0,.3)';
    },
    hoverImageGradient() {
      return 'to bottom, rgba(0,0,0,.3), rgba(0,0,0,.4)';
    },
  },
  created() {
    this.tab = this.mainTabValue;
  },
  methods: {
    getActivityElapsedTimeMins(activity) {
      let endDt = null;
      const startDt = new Date(activity.startDateTimeLt);

      if (activity && activity.endDateTimeLt) {
        endDt = new Date(activity.endDateTimeLt);
      } else {
        endDt = new Date().setSeconds(0, 0);
      }

      const diffTimeMs = Math.abs(endDt - startDt);
      const diffTimeMins = diffTimeMs / (1000 * 60);
      return diffTimeMins;
    },
    showUpdateWarning(latestActivity) {
      const updateWarningHours = this.overdueHours;
      if (updateWarningHours === -1) return false;

      const { lastUpdatedAtLt } = latestActivity;
      const lastUpdatedAtDate = new Date(lastUpdatedAtLt);
      const currentDate = new Date(this.lastRefreshLt);

      const diffTimeMs = Math.abs(currentDate - lastUpdatedAtDate);
      const diffTimeHrs = (diffTimeMs / (1000 * 60 * 60)).toFixed(1);
      if (diffTimeHrs >= updateWarningHours) return true;
      return false;
    },
    isActivityInvalid(report) {
      const { latestActivity, validationReport } = report;
      const { invalidActivities } = validationReport;
      if (invalidActivities.length > 0) {
        const isLatestActivityInvalid = invalidActivities
          .map((x) => x.id)
          .includes(latestActivity.id);
        return isLatestActivityInvalid;
      }
      return false;
    },
    getInvalidActivityIssueMessage(report) {
      const { latestActivity, validationReport } = report;
      const { invalidActivities } = validationReport;

      const invalidActivity = invalidActivities.find((x) => x.id === latestActivity.id);
      return invalidActivity.issueMessage;
    },
    changeTab() {
      const { activitiesTabValue, mainTabValue } = this;
      switch (this.tab) {
        case mainTabValue:
          this.tab = activitiesTabValue;
          break;
        default:
          this.tab = mainTabValue;
          break;
      }
    },
    goToVoyageDetails(reportId) {
      this.$router.push({ name: 'voyagedetails', params: { id: reportId } });
    },
  },
};
</script>

<style scoped>
.report-card-header {
  cursor: pointer;
}

.update-warning {
  outline: solid red !important;
  color: red !important;
  font-weight: bold;
}

.active-tab {
  background-color: white;
}
</style>
